/**
 * Scoring local du Diagnostic Patrimonial (sans données inventées côté preuve).
 * Le score reflète uniquement les réponses du formulaire.
 */
export const DIAGNOSTIC_STORAGE_KEY = 'patrimonea_diagnostic_v1';
export const DIAGNOSTIC_DRAFT_KEY = 'patrimonea_diagnostic_draft_v1';

/** Phase éducative — bonne réponse : biens + dettes. */
export const EDU_PAT = [
  { value: 'ensemble', label: "Un patrimoine c'est l'ensemble des biens" },
  { value: 'biens', label: "Un patrimoine c'est l'ensemble des biens et des dettes" },
  { value: 'positif', label: "Un patrimoine c'est uniquement le solde positif" },
];
export const EDU_PAT_CORRECT = 'biens';

/** Étapes du tunnel public Diagnostic.vue */
export const DIAGNOSTIC_PHASES = [
  { id: 'edu', label: 'Comprendre' },
  { id: 'situation', label: 'Situation' },
  { id: 'perso', label: 'Coordonnées' },
  { id: 'patrimoine', label: 'Patrimoine' },
  { id: 'proof', label: '' },
  { id: 'closing', label: '' },
  { id: 'form', label: 'Charges' },
];

export const STATUTS = [
  { value: 'cadre', label: 'Cadre / cadre dirigeant' },
  { value: 'dirigeant', label: 'Dirigeant / entrepreneur' },
  { value: 'liberal', label: 'Profession libérale' },
];

export const TRANCHES_REVENU = [
  { value: '0_500k', label: '0 à 499 999 FCFA / mois', points: 10 },
  { value: '500k_1m', label: '500 000 à 749 999 FCFA / mois', points: 10 },
  { value: '1m_2m', label: '750 000 à 1 499 000 FCFA / mois', points: 12 },
  { value: '2m_5m', label: '1 500 000 à 4M FCFA / mois', points: 18 },
  { value: '5m_plus', label: 'Plus de 4M FCFA / mois', points: 24 },
];

export const TRANCHES_ACTIFS = [
  { value: 'lt_50', label: 'Moins de 50 M FCFA', points: 8 },
  { value: '50_200', label: '50 à 200 M FCFA', points: 16 },
  { value: '200_500', label: '200 à 500 M FCFA', points: 22 },
  { value: 'gt_500', label: 'Plus de 500 M FCFA', points: 28 },
];

export const CLASSES_ACTIFS = [
  { value: 'immobilier', label: 'Immobilier' },
  { value: 'epargne', label: 'Épargne / liquidités' },
  { value: 'placements', label: 'Placements financiers' },
  { value: 'entreprise', label: 'Parts d’entreprise' },
];

export const PRIORITES = [
  { value: 'retraite', label: 'Préparer ma retraite' },
  { value: 'bien', label: 'Acquérir un bien' },
  { value: 'dette', label: 'Être désendetté' },
  { value: 'diversification', label: 'Diversifier mes actifs' },
  { value: 'protection', label: 'Protéger mes proches' },
  { value: 'autre', label: 'Autres'},
];


/** Champs charges alignés sur FormApat (clés identiques pour fusion ultérieure). */
export function emptyChargesAnswers() {
  return {
    loyer: null,
    telephone: null,
    electricite: null,
    gaz: null,
    eau: null,
    abonnement: null,
    ordures: null,
    reparation: null,
    fournitures: null,
    salaire_personnel_maison: null,
    popote: null,
    loyer_autre: null,
    pret_voiture: null,
    transport_public: null,
    assurance_voiture: null,
    carburant: null,
    entretien_transport: null,
    autres_transport: null,
    habitat: null,
    sante: null,
    autresAssurance: null,
    pret_personnel: null,
    pret_etudiant: null,
    epargne: null,
    salaire: null,
    fonction: '',
    adresse: '',
    date_born: '',
    commune_born: '',
    departement_born: '',
  };
}

export function emptyDiagnosticAnswers() {
  return {
    eduPat: '',
    ageRange: '',
    statut: '',
    revenu: '',
    actifs: '',
    classes: [],
    /** Un ou plusieurs objectifs (valeurs PRIORITES). */
    priorites: [],
    /** Texte libre saisi quand 'autre' est sélectionné parmi les objectifs. */
    prioriteAutre: '',
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    charges: emptyChargesAnswers(),
  };
}

/** Normalise l’ancien champ `priorite` (string) vers `priorites` (array). */
export function normalizePriorites(answers = {}) {
  if (Array.isArray(answers.priorites) && answers.priorites.length) {
    return [...new Set(answers.priorites.map(String).filter(Boolean))];
  }
  const legacy = answers.priorite;
  if (Array.isArray(legacy)) {
    return [...new Set(legacy.map(String).filter(Boolean))];
  }
  if (legacy) return [String(legacy)];
  return [];
}

/** @param {string} autreText - texte libre remplaçant le label générique « Autres » quand fourni. */
export function prioriteLabels(values = [], autreText = '') {
  const set = new Set((values || []).map(String));
  const autre = String(autreText || '').trim();
  return PRIORITES.filter((p) => set.has(p.value)).map((p) =>
    p.value === 'autre' && autre ? autre : p.label
  );
}

export function saveDiagnosticDraft({ phase, answers }) {
  try {
    localStorage.setItem(
      DIAGNOSTIC_DRAFT_KEY,
      JSON.stringify({
        phase,
        answers: {
          ...answers,
          classes: [...(answers.classes || [])],
          priorites: normalizePriorites(answers),
          charges: { ...(answers.charges || emptyChargesAnswers()) },
        },
        ts: Date.now(),
      }),
    );
  } catch (_) {
    /* ignore quota / private mode */
  }
}

export function loadDiagnosticDraft() {
  try {
    const raw = localStorage.getItem(DIAGNOSTIC_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export function clearDiagnosticDraft() {
  try {
    localStorage.removeItem(DIAGNOSTIC_DRAFT_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function computeDiagnosticScore(answers = {}) {
  let score = 28;
  const revenu = TRANCHES_REVENU.find((t) => t.value === answers.revenu);
  const actifs = TRANCHES_ACTIFS.find((t) => t.value === answers.actifs);
  if (revenu) score += revenu.points;
  if (actifs) score += actifs.points;
  const nClasses = Array.isArray(answers.classes) ? answers.classes.length : 0;
  score += Math.min(18, nClasses * 5);
  if (answers.dettes === 'oui') score -= 8;
  if (answers.dettes === 'non') score += 6;
  if (answers.horizon === 'long') score += 6;
  if (answers.horizon === 'moyen') score += 3;
  const nPriorites = normalizePriorites(answers).length;
  if (nPriorites > 0) score += Math.min(8, 2 + nPriorites * 2);
  return Math.max(18, Math.min(92, Math.round(score)));
}

export function buildDiagnosticInsights(answers = {}, score) {
  const vigilance = [];
  const leviers = [];

  if ((answers.classes || []).length <= 1) {
    vigilance.push('Concentration forte sur une ou deux classes d’actifs.');
    leviers.push('Diversifier progressivement (immobilier, liquidités, placements).');
  } else {
    vigilance.push('Actifs présents sur plusieurs classes — à consolider pour une vision unique.');
  }

  if (answers.dettes === 'oui') {
    vigilance.push('Engagements / dettes à cartographier avant tout arbitrage.');
    leviers.push('Prioriser le remboursement stratégique des dettes coûteuses.');
  } else {
    vigilance.push('Peu de visibilité consolidée peut masquer des opportunités d’optimisation.');
  }

  const prioMap = {
    retraite: 'Construire un plan de revenus futurs aligné sur votre horizon.',
    bien: 'Sécuriser le financement et le timing d’acquisition.',
    dette: 'Prioriser le désendettement stratégique des engagements coûteux.',
    diversification: 'Rééquilibrer le portefeuille selon votre profil de risque.',
    protection: 'Couvrir les risques famille / activité avec des solutions adaptées.',
  };
  const selected = normalizePriorites(answers);
  selected.forEach((key) => {
    if (prioMap[key]) leviers.push(prioMap[key]);
  });
  if (!selected.length) {
    leviers.push('Définir une priorité patrimoniale claire pour les 12 prochains mois.');
  }

  while (vigilance.length < 3) {
    vigilance.push('Absence de revue périodique structurée avec un conseiller dédié.');
  }
  while (leviers.length < 3) {
    leviers.push('Mettre en place un tableau de bord unique et un rythme de suivi.');
  }

  let label = 'Structuration à renforcer';
  if (score >= 70) label = 'Bonne base — à structurer';
  else if (score >= 50) label = 'Structuration partielle';

  return {
    score,
    label,
    vigilance: vigilance.slice(0, 3),
    leviers: leviers.slice(0, 3),
    segment: answers.statut || 'cadre',
    priority: selected[0] || '',
    priorities: selected,
  };
}

export function saveDiagnosticResult(payload) {
  try {
    sessionStorage.setItem(DIAGNOSTIC_STORAGE_KEY, JSON.stringify({ ...payload, ts: Date.now() }));
  } catch (_) {
    /* ignore */
  }
}

export function loadDiagnosticResult() {
  try {
    const raw = sessionStorage.getItem(DIAGNOSTIC_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

export function formatDiagnosticMessage(answers, insights) {
  const lines = [
    `Diagnostic patrimonial — score ${insights.score}/100 (${insights.label})`,
    `Statut: ${answers.statut || '—'} · Pays: ${answers.pays || '—'} · Revenu: ${answers.revenu || '—'}`,
    `Actifs: ${answers.actifs || '—'} · Classes: ${(answers.classes || []).join(', ') || '—'}`,
    `Dettes: ${answers.dettes || '—'} · Objectifs: ${prioriteLabels(normalizePriorites(answers), answers.prioriteAutre).join(', ') || '—'} · Horizon: ${answers.horizon || '—'}`,
    `Vigilance: ${insights.vigilance.join(' | ')}`,
    `Leviers: ${insights.leviers.join(' | ')}`,
  ];
  return lines.join('\n');
}
